import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Users, Award, Loader2, Search, RefreshCw, Plus, UserMinus, Mail, Phone, Calendar, Shield, CheckCircle, XCircle, AlertTriangle, UserPlus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CoachManagement({ user }) {
  const [coaches, setCoaches] = useState([]);
  const [headCoachIds, setHeadCoachIds] = useState(new Set());
  const [teams, setTeams] = useState([]);
  const [programmes, setProgrammes] = useState([]);
  const [rateDialog, setRateDialog] = useState({ open: false, coach: null });
  const [newRate, setNewRate] = useState({
    applies_to: "programme", scope_id: "", basis: "per_session", rate: "" });
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("coaches"); // "coaches" or "applications"
  
  // Assign team dialog
  const [assignDialog, setAssignDialog] = useState({ open: false, coach: null });
  const [selectedTeamId, setSelectedTeamId] = useState("");
  const [assigning, setAssigning] = useState(false);
  
  // Approval dialog
  const [approvalResult, setApprovalResult] = useState(null);
  
  // Activation state
  const [activating, setActivating] = useState(false);
  const [activationResults, setActivationResults] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [coachesRes, teamsRes, appsRes] = await Promise.all([
        axios.get(`${API}/admin/coaches`, { withCredentials: true }),
        axios.get(`${API}/academy/teams`, { withCredentials: true }),
        axios.get(`${API}/admin/coach-applications`, { withCredentials: true })
      ]);
      setCoaches(coachesRes.data || []);
      setTeams(teamsRes.data || []);
      setApplications(appsRes.data || []);
      try {
        const terms = await axios.get(`${API}/academy/terms`, { withCredentials: true });
        setProgrammes(terms.data || []);
      } catch (_) { /* non-fatal */ }
      try {
        const hc = await axios.get(`${API}/kit/admin/coaches`, { withCredentials: true });
        setHeadCoachIds(new Set((hc.data.coaches || []).filter((c) => c.is_head_coach).map((c) => c.id)));
      } catch (_) { /* non-fatal */ }
      return coachesRes.data || [];
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load coach data");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleActivateApproved = async () => {
    setActivating(true);
    try {
      const response = await axios.post(
        `${API}/admin/coaches/activate-approved`,
        {},
        { withCredentials: true }
      );
      if (response.data.activated_count > 0) {
        setActivationResults(response.data.results);
        toast.success(`Activated ${response.data.activated_count} coaches!`);
      } else {
        toast.info("All approved coaches are already activated");
      }
      fetchData();
    } catch (error) {
      console.error("Error activating:", error);
      toast.error(error.response?.data?.detail || "Failed to activate coaches");
    } finally {
      setActivating(false);
    }
  };

  const handleApprove = async (applicationId) => {
    try {
      const response = await axios.post(
        `${API}/admin/coach-applications/${applicationId}/approve`,
        {},
        { withCredentials: true }
      );
      setApprovalResult(response.data);
      toast.success("Coach application approved!");
      fetchData();
    } catch (error) {
      console.error("Error approving:", error);
      toast.error(error.response?.data?.detail || "Failed to approve application");
    }
  };

  const handleReject = async (applicationId) => {
    if (!confirm("Are you sure you want to reject this application?")) return;
    
    try {
      await axios.post(
        `${API}/admin/coach-applications/${applicationId}/reject`,
        {},
        { withCredentials: true }
      );
      toast.success("Application rejected");
      fetchData();
    } catch (error) {
      console.error("Error rejecting:", error);
      toast.error("Failed to reject application");
    }
  };

  const handleAssignTeam = async () => {
    if (!selectedTeamId || !assignDialog.coach) return;
    
    setAssigning(true);
    try {
      await axios.post(
        `${API}/admin/coaches/${assignDialog.coach.id}/assign-team?team_id=${selectedTeamId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Coach assigned to team!");
      setAssignDialog({ open: false, coach: null });
      setSelectedTeamId("");
      fetchData();
    } catch (error) {
      console.error("Error assigning team:", error);
      toast.error(error.response?.data?.detail || "Failed to assign team");
    } finally {
      setAssigning(false);
    }
  };

  // What a coach is paid, used by the pay sheet under Attendance -> Coaches.
  // A coach can hold a default rate plus overrides per programme or team.
  const openPayRates = (coach) => {
    setRateDialog({ open: true, coach });
    setNewRate({ applies_to: "programme", scope_id: "", basis: "per_session", rate: "" });
  };

  const saveDefaultRate = async (coach, basis, rate) => {
    try {
      await axios.put(`${API}/academy/attendance-hub/coach-rate/${coach.id}`,
        { basis, rate: Number(rate) }, { withCredentials: true });
      toast.success(`${coach.name}: $${Number(rate).toFixed(2)} ${basis === "per_hour" ? "per hour" : "per session"}`);
      await fetchData();
      setRateDialog((d) => ({ ...d, coach: { ...d.coach, coach_pay_basis: basis, coach_pay_rate: Number(rate) } }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save that rate");
    }
  };

  const saveOverride = async (coach) => {
    try {
      const r = await axios.put(`${API}/academy/attendance-hub/coach-rate/${coach.id}`, {
        basis: newRate.basis,
        rate: Number(newRate.rate),
        applies_to: newRate.applies_to,
        scope_id: newRate.scope_id,
        label: newRate.applies_to === "team"
          ? (teams.find((t) => t.id === newRate.scope_id)?.name || "team")
          : (programmes.find((p) => p.id === newRate.scope_id)?.name || "programme"),
      }, { withCredentials: true });
      toast.success(`Extra rate saved: $${r.data.rate.toFixed(2)}`);
      setNewRate({ ...newRate, scope_id: "", rate: "" });
      const fresh = await fetchData();
      const updated = (fresh || []).find((c) => c.id === coach.id);
      if (updated) setRateDialog((d) => ({ ...d, coach: updated }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save that rate");
    }
  };

  const removeOverride = async (coach, rule) => {
    try {
      await axios.delete(`${API}/academy/attendance-hub/coach-rate/${coach.id}`, {
        params: { applies_to: rule.applies_to, scope_id: rule.scope_id },
        withCredentials: true,
      });
      toast.success("Rate removed");
      const fresh = await fetchData();
      const updated = (fresh || []).find((c) => c.id === coach.id);
      if (updated) setRateDialog((d) => ({ ...d, coach: updated }));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not remove that rate");
    }
  };


  const toggleHeadCoach = async (coach) => {
    const makeHead = !headCoachIds.has(coach.id);
    try {
      await axios.post(`${API}/kit/admin/set-head-coach`, { coach_id: coach.id, is_head_coach: makeHead }, { withCredentials: true });
      setHeadCoachIds((prev) => {
        const n = new Set(prev);
        makeHead ? n.add(coach.id) : n.delete(coach.id);
        return n;
      });
      toast.success(makeHead ? `${coach.name} is now a Head Coach` : `${coach.name} is no longer a Head Coach`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update head coach");
    }
  };

  const handleUnassignTeam = async (coachId, teamId, teamName) => {
    if (!confirm(`Remove coach from ${teamName}?`)) return;
    
    try {
      await axios.post(
        `${API}/admin/coaches/${coachId}/unassign-team?team_id=${teamId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Coach removed from team");
      fetchData();
    } catch (error) {
      console.error("Error unassigning:", error);
      toast.error("Failed to remove from team");
    }
  };

  const handleRemoveCoachRole = async (coachId, coachName) => {
    if (!confirm(`Remove coach role from ${coachName}? This will also remove them from all teams.`)) return;
    
    try {
      await axios.delete(
        `${API}/admin/coaches/${coachId}/remove-role`,
        { withCredentials: true }
      );
      toast.success("Coach role removed");
      fetchData();
    } catch (error) {
      console.error("Error removing role:", error);
      toast.error("Failed to remove coach role");
    }
  };

  const filteredCoaches = coaches.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(term) ||
      (c.email || "").toLowerCase().includes(term)
    );
  });

  const pendingApplications = applications.filter(a => a.status === "pending");
  const approvedApplications = applications.filter(a => a.status === "approved");
  const rejectedApplications = applications.filter(a => a.status === "rejected");
  
  // Find approved applications without user accounts (need activation)
  const unactivatedApproved = approvedApplications.filter(a => !a.user_id);

  // Get teams not already assigned to the coach
  const getAvailableTeams = (coach) => {
    const assignedIds = coach?.academy_coach_teams || [];
    return teams.filter(t => !assignedIds.includes(t.id));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Alert for unactivated approved coaches */}
      {unactivatedApproved.length > 0 && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/30">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-200">
                <strong>{unactivatedApproved.length} approved coach{unactivatedApproved.length > 1 ? 'es' : ''}</strong> need account activation to login
              </span>
            </div>
            <Button
              onClick={handleActivateApproved}
              disabled={activating}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {activating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Activate All
            </Button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/academy">
              <Button variant="ghost" className="text-white hover:text-blue-200">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Academy
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Award className="w-6 h-6 text-orange-400" />
              Coach Management
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-orange-500/80 text-white text-lg px-4 py-1">
              {coaches.length} coaches
            </Badge>
            {pendingApplications.length > 0 && (
              <Badge className="bg-yellow-500 text-black text-lg px-4 py-1">
                {pendingApplications.length} pending
              </Badge>
            )}
            <Button onClick={fetchData} variant="outline" size="sm" className="border-slate-600">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === "coaches" ? "default" : "outline"}
            onClick={() => setActiveTab("coaches")}
            className={activeTab === "coaches" ? "bg-orange-600 hover:bg-orange-700" : "border-slate-600"}
          >
            <Users className="w-4 h-4 mr-2" />
            Active Coaches ({coaches.length})
          </Button>
          <Button
            variant={activeTab === "applications" ? "default" : "outline"}
            onClick={() => setActiveTab("applications")}
            className={activeTab === "applications" ? "bg-yellow-600 hover:bg-yellow-700" : "border-slate-600"}
          >
            <Award className="w-4 h-4 mr-2" />
            Applications ({pendingApplications.length} pending)
          </Button>
        </div>

        {activeTab === "coaches" && (
          <>
            {/* Search */}
            <Card className="p-4 bg-slate-900/50 border-slate-700 mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search coaches..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-slate-800 border-slate-600 text-white"
                />
              </div>
            </Card>

            {/* Coaches Table */}
            <Card className="bg-slate-900/50 border-slate-700 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-slate-800/50">
                    <TableHead className="text-slate-300">Coach</TableHead>
                    <TableHead className="text-slate-300">Contact</TableHead>
                    <TableHead className="text-slate-300">Certifications</TableHead>
                    <TableHead className="text-slate-300">Assigned Teams</TableHead>
                    <TableHead className="text-slate-300">Head Coach</TableHead>
                    <TableHead className="text-slate-300">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCoaches.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-slate-400 py-8">
                        No coaches found. Approve coach applications to add coaches.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCoaches.map((coach) => (
                      <TableRow key={coach.id} className="border-slate-700 hover:bg-slate-800/30">
                        <TableCell>
                          <div>
                            <p className="font-medium text-white">{coach.name}</p>
                            {coach.nroc_registered && (
                              <Badge className="bg-blue-500/20 text-blue-400 text-xs mt-1">NROC</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <p className="text-slate-300 flex items-center gap-1">
                              <Mail className="w-3 h-3" /> {coach.email}
                            </p>
                            {coach.phone && (
                              <p className="text-slate-400 flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {coach.phone}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(coach.coaching_certifications || []).map((cert, i) => (
                              <Badge key={i} variant="outline" className="text-xs border-slate-600 text-slate-300">
                                {cert}
                              </Badge>
                            ))}
                            {(!coach.coaching_certifications || coach.coaching_certifications.length === 0) && (
                              <span className="text-slate-500 text-sm">None listed</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {(coach.teams || []).length === 0 ? (
                              <span className="text-slate-500 text-sm">Not assigned</span>
                            ) : (
                              coach.teams.map((team) => (
                                <div key={team.id} className="flex items-center gap-2">
                                  <Badge className="bg-emerald-500/20 text-emerald-400">
                                    {team.name}
                                  </Badge>
                                  <button
                                    onClick={() => handleUnassignTeam(coach.id, team.id, team.name)}
                                    className="text-red-400 hover:text-red-300 p-1"
                                    title="Remove from team"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant={headCoachIds.has(coach.id) ? "default" : "outline"}
                            className={headCoachIds.has(coach.id) ? "bg-fuchsia-600 hover:bg-fuchsia-500 h-7 text-xs" : "h-7 text-xs border-slate-600"}
                            onClick={() => toggleHeadCoach(coach)}
                            data-testid={`head-coach-toggle-${coach.id}`}
                          >
                            {headCoachIds.has(coach.id) ? "Head Coach ✓" : "Make Head"}
                          </Button>
                          <button
                            onClick={() => openPayRates(coach)}
                            className="block mt-1 text-[11px] text-blue-400 underline hover:text-blue-300"
                            data-testid={`pay-rate-${coach.id}`}
                          >
                            {coach.coach_pay_rate
                              ? `$${coach.coach_pay_rate} ${coach.coach_pay_basis === "per_hour" ? "/hr" : "/session"}`
                              : "set pay rate"}
                            {!!(coach.coach_pay_rates || []).length && (
                              <span className="text-slate-400">
                                {" "}+{coach.coach_pay_rates.length} more
                              </span>
                            )}
                          </button>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setAssignDialog({ open: true, coach })}
                              className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/20"
                              disabled={getAvailableTeams(coach).length === 0}
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Assign Team
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRemoveCoachRole(coach.id, coach.name)}
                              className="border-red-600 text-red-400 hover:bg-red-600/20"
                            >
                              <UserMinus className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </>
        )}

        {activeTab === "applications" && (
          <div className="space-y-6">
            {/* Pending Applications */}
            <Card className="bg-slate-900/50 border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-yellow-400 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Pending Applications ({pendingApplications.length})
              </h3>
              {pendingApplications.length === 0 ? (
                <p className="text-slate-400">No pending applications</p>
              ) : (
                <div className="space-y-4">
                  {pendingApplications.map((app) => (
                    <Card key={app.id} className="p-4 bg-slate-800/50 border-yellow-500/30">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h4 className="text-lg font-semibold text-white">{app.full_name}</h4>
                            {app.is_section_member && (
                              <Badge className="bg-emerald-500/20 text-emerald-400">Section Member</Badge>
                            )}
                            {app.nroc_registered && (
                              <Badge className="bg-blue-500/20 text-blue-400">NROC</Badge>
                            )}
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm text-slate-400 mb-3">
                            <p className="flex items-center gap-1"><Mail className="w-4 h-4" /> {app.email}</p>
                            <p className="flex items-center gap-1"><Phone className="w-4 h-4" /> {app.phone}</p>
                            {app.birthdate && (
                              <p className="flex items-center gap-1"><Calendar className="w-4 h-4" /> {new Date(app.birthdate).toLocaleDateString()}</p>
                            )}
                            {app.membership_number && (
                              <p>Membership: {app.membership_number}</p>
                            )}
                          </div>
                          {app.coaching_certifications?.length > 0 && (
                            <div className="mb-2">
                              <p className="text-xs text-slate-500 mb-1">Certifications:</p>
                              <div className="flex flex-wrap gap-1">
                                {app.coaching_certifications.map((cert, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">{cert}</Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          {app.currently_coaching?.length > 0 && (
                            <div>
                              <p className="text-xs text-slate-500 mb-1">Currently coaching:</p>
                              <p className="text-sm text-slate-300">{app.currently_coaching.join(", ")}</p>
                            </div>
                          )}
                          <p className="text-xs text-slate-500 mt-2">
                            Applied: {new Date(app.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <Button
                            onClick={() => handleApprove(app.id)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => handleReject(app.id)}
                            variant="outline"
                            className="border-red-600 text-red-400 hover:bg-red-600/20"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </Card>

            {/* Approved Applications History */}
            <Card className="bg-slate-900/50 border-slate-700 p-6">
              <h3 className="text-lg font-semibold text-emerald-400 mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                Approved ({approvedApplications.length})
              </h3>
              {approvedApplications.length === 0 ? (
                <p className="text-slate-400">No approved applications</p>
              ) : (
                <div className="space-y-2">
                  {approvedApplications.map((app) => (
                    <div key={app.id} className={`flex items-center justify-between p-3 rounded-lg ${app.user_id ? 'bg-slate-800/30' : 'bg-yellow-500/10 border border-yellow-500/30'}`}>
                      <div>
                        <p className="text-white font-medium">{app.full_name}</p>
                        <p className="text-sm text-slate-400">{app.email}</p>
                      </div>
                      <div className="text-right flex items-center gap-2">
                        {!app.user_id ? (
                          <Badge className="bg-yellow-500/20 text-yellow-400">Needs Activation</Badge>
                        ) : (
                          <Badge className="bg-emerald-500/20 text-emerald-400">Active</Badge>
                        )}
                        <p className="text-xs text-slate-500">
                          {app.approved_at ? new Date(app.approved_at).toLocaleDateString() : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            {/* Rejected Applications History */}
            {rejectedApplications.length > 0 && (
              <Card className="bg-slate-900/50 border-slate-700 p-6">
                <h3 className="text-lg font-semibold text-red-400 mb-4 flex items-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Rejected ({rejectedApplications.length})
                </h3>
                <div className="space-y-2">
                  {rejectedApplications.map((app) => (
                    <div key={app.id} className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg opacity-60">
                      <div>
                        <p className="text-white font-medium">{app.full_name}</p>
                        <p className="text-sm text-slate-400">{app.email}</p>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-red-500/20 text-red-400">Rejected</Badge>
                        <p className="text-xs text-slate-500 mt-1">
                          {app.rejected_at ? new Date(app.rejected_at).toLocaleDateString() : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Assign Team Dialog */}
      {/* What a coach is paid: a default, plus any programme or team that pays differently */}
      <Dialog open={rateDialog.open}
        onOpenChange={(open) => !open && setRateDialog({ open: false, coach: null })}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Pay rates — {rateDialog.coach?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              The most specific rate wins: a team rate beats a programme rate, which beats the default.
            </DialogDescription>
          </DialogHeader>

          {rateDialog.coach && (
            <div className="space-y-5">
              <div>
                <p className="text-sm font-semibold mb-2">Default rate</p>
                <div className="flex items-end gap-2">
                  <select defaultValue={rateDialog.coach.coach_pay_basis || "per_session"}
                    id="default-basis"
                    className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
                    data-testid="default-basis">
                    <option value="per_session">Per session</option>
                    <option value="per_hour">Per hour</option>
                  </select>
                  <Input type="number" min="0" step="0.01" id="default-rate"
                    defaultValue={rateDialog.coach.coach_pay_rate || ""}
                    placeholder="100.00"
                    className="w-32 bg-slate-800 border-slate-700"
                    data-testid="default-rate" />
                  <Button size="sm" onClick={() => saveDefaultRate(
                    rateDialog.coach,
                    document.getElementById("default-basis").value,
                    document.getElementById("default-rate").value)}
                    data-testid="save-default-rate">Save</Button>
                </div>
              </div>

              <div>
                <p className="text-sm font-semibold mb-2">Paid differently for...</p>
                {!(rateDialog.coach.coach_pay_rates || []).length && (
                  <p className="text-xs text-slate-500 mb-2">
                    Nothing yet — everything uses the default rate.
                  </p>
                )}
                {(rateDialog.coach.coach_pay_rates || []).map((r) => (
                  <div key={`${r.applies_to}-${r.scope_id}`}
                    className="flex items-center justify-between py-1.5 text-sm border-b border-slate-800 last:border-0"
                    data-testid={`rate-rule-${r.scope_id}`}>
                    <span className="text-slate-200">
                      {r.label || r.applies_to}
                      <span className="text-xs text-slate-500 ml-2">
                        ${Number(r.rate).toFixed(2)} {r.basis === "per_hour" ? "/hr" : "/session"}
                      </span>
                    </span>
                    <button onClick={() => removeOverride(rateDialog.coach, r)}
                      className="text-xs text-red-400 hover:text-red-300 underline"
                      data-testid={`remove-rate-${r.scope_id}`}>
                      remove
                    </button>
                  </div>
                ))}

                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <select value={newRate.applies_to}
                    onChange={(e) => setNewRate({ ...newRate, applies_to: e.target.value, scope_id: "" })}
                    className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
                    data-testid="override-type">
                    <option value="programme">A programme</option>
                    <option value="team">A team</option>
                  </select>
                  <select value={newRate.scope_id}
                    onChange={(e) => setNewRate({ ...newRate, scope_id: e.target.value })}
                    className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
                    data-testid="override-scope">
                    <option value="">Choose...</option>
                    {(newRate.applies_to === "team" ? teams : programmes).map((o) => (
                      <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                  </select>
                  <select value={newRate.basis}
                    onChange={(e) => setNewRate({ ...newRate, basis: e.target.value })}
                    className="bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm"
                    data-testid="override-basis">
                    <option value="per_session">Per session</option>
                    <option value="per_hour">Per hour</option>
                  </select>
                  <div className="flex gap-2">
                    <Input type="number" min="0" step="0.01" value={newRate.rate}
                      onChange={(e) => setNewRate({ ...newRate, rate: e.target.value })}
                      placeholder="60.00"
                      className="bg-slate-800 border-slate-700"
                      data-testid="override-rate" />
                    <Button size="sm" variant="outline"
                      disabled={!newRate.scope_id || !(Number(newRate.rate) > 0)}
                      onClick={() => saveOverride(rateDialog.coach)}
                      data-testid="add-override-rate">Add</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      <Dialog open={assignDialog.open} onOpenChange={(open) => !open && setAssignDialog({ open: false, coach: null })}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Assign Team to {assignDialog.coach?.name}</DialogTitle>
            <DialogDescription className="text-slate-400">
              Select a team to assign this coach to
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
              <SelectTrigger className="bg-slate-800 border-slate-600 text-white">
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                {getAvailableTeams(assignDialog.coach).map((team) => (
                  <SelectItem key={team.id} value={team.id} className="text-white hover:bg-slate-700">
                    {team.name} {team.age_group && `(${team.age_group})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialog({ open: false, coach: null })} className="border-slate-600">
              Cancel
            </Button>
            <Button
              onClick={handleAssignTeam}
              disabled={!selectedTeamId || assigning}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {assigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Result Dialog */}
      <Dialog open={!!approvalResult} onOpenChange={() => setApprovalResult(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-emerald-400 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Coach Approved Successfully
            </DialogTitle>
          </DialogHeader>
          {approvalResult && (
            <div className="py-4 space-y-3">
              <p className="text-white"><strong>Coach:</strong> {approvalResult.coach_name}</p>
              <p className="text-white"><strong>Email:</strong> {approvalResult.coach_email}</p>
              {approvalResult.user_created ? (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <p className="text-yellow-400 font-medium mb-2">New Account Created</p>
                  <p className="text-slate-300 text-sm">Temporary Password: <code className="bg-slate-800 px-2 py-1 rounded">{approvalResult.temp_password}</code></p>
                  <p className="text-slate-400 text-xs mt-2">Please share this with the coach. They should change it on first login.</p>
                </div>
              ) : (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                  <p className="text-blue-400 font-medium">Linked to Existing Account</p>
                  <p className="text-slate-400 text-sm mt-1">{approvalResult.login_instructions}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setApprovalResult(null)} className="bg-emerald-600 hover:bg-emerald-700">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activation Results Dialog */}
      <Dialog open={!!activationResults} onOpenChange={() => setActivationResults(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-emerald-400 flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Coaches Activated
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              The following coaches now have active accounts
            </DialogDescription>
          </DialogHeader>
          {activationResults && (
            <div className="py-4 space-y-3">
              {activationResults.map((result, i) => (
                <Card key={i} className="p-4 bg-slate-800/50 border-slate-700">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white font-medium">{result.coach_name}</p>
                      <p className="text-sm text-slate-400">{result.coach_email}</p>
                    </div>
                    <Badge className={result.action === "created_new_user" ? "bg-yellow-500/20 text-yellow-400" : "bg-blue-500/20 text-blue-400"}>
                      {result.action === "created_new_user" ? "New Account" : "Linked Existing"}
                    </Badge>
                  </div>
                  {result.temp_password && (
                    <div className="mt-2 p-2 bg-yellow-500/10 rounded">
                      <p className="text-xs text-yellow-400">Temporary Password: <code className="bg-slate-800 px-1 rounded">{result.temp_password}</code></p>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setActivationResults(null)} className="bg-emerald-600 hover:bg-emerald-700">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
